<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="bg-card rounded-lg border p-6">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-lg font-semibold">MCP Access Token</h3>
          <p class="text-sm text-muted-foreground">
            Generate and manage your access token for MCP (Model Context Protocol) integration
          </p>
        </div>
        <div class="text-right">
          <p class="text-2xl font-bold">{{ tokenMetadata.has_token ? '✓' : '✗' }}</p>
          <p class="text-sm text-muted-foreground">{{ tokenMetadata.has_token ? 'Active' : 'None' }}</p>
        </div>
      </div>
    </div>

    <!-- Token Status -->
    <div class="bg-card rounded-lg border p-6">
      <h4 class="text-md font-semibold mb-4">Token Status</h4>
      <div v-if="isLoading" class="text-center py-4">
        <svg
          class="mx-auto h-6 w-6 animate-spin text-muted-foreground"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p class="mt-2 text-sm text-muted-foreground">Loading token status...</p>
      </div>
      <div v-else class="space-y-4">
        <div class="flex items-center justify-between p-4 bg-muted/50 rounded-md">
          <div>
            <p class="text-sm font-medium">Token Status</p>
            <p class="text-sm text-muted-foreground">
              {{ tokenMetadata.has_token ? 'You have an active access token' : 'No access token generated' }}
            </p>
          </div>
          <div class="text-right">
            <p v-if="tokenMetadata.has_token && tokenMetadata.created_at" class="text-xs text-muted-foreground">
              Created: {{ formatDate(tokenMetadata.created_at) }}
            </p>
          </div>
        </div>

        <!-- Token Actions -->
        <div class="flex gap-4">
          <button
            v-if="!tokenMetadata.has_token"
            @click="generateToken"
            :disabled="isGenerating"
            class="flex-1 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4"
          >
            <svg
              v-if="isGenerating"
              class="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ isGenerating ? 'Generating...' : 'Generate Token' }}
          </button>
          <button
            v-else
            @click="regenerateToken"
            :disabled="isRegenerating"
            class="flex-1 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4"
          >
            <svg
              v-if="isRegenerating"
              class="mr-2 h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ isRegenerating ? 'Regenerating...' : 'Regenerate Token' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Token Display (One-time show) -->
    <div v-if="displayedToken" class="bg-card rounded-lg border p-6">
      <div class="space-y-4">
        <div class="flex items-start justify-between">
          <div>
            <h4 class="text-md font-semibold text-yellow-600 dark:text-yellow-400">⚠️ Important: Save This Token</h4>
            <p class="text-sm text-muted-foreground mt-1">
              This token will only be shown once. Make sure to copy and save it securely.
            </p>
          </div>
          <button
            @click="closeTokenDisplay"
            class="text-muted-foreground hover:text-foreground"
          >
            <svg
              class="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="bg-muted rounded-md p-4">
          <div class="flex items-center justify-between gap-4">
            <code class="text-sm font-mono break-all flex-1">{{ displayedToken }}</code>
            <button
              @click="copyToken"
              class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring hover:bg-background h-9 px-3 whitespace-nowrap"
            >
              <svg
                v-if="!copied"
                class="mr-2 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <svg
                v-else
                class="mr-2 h-4 w-4 text-green-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              {{ copied ? 'Copied!' : 'Copy' }}
            </button>
          </div>
        </div>

        <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
          <p class="text-sm text-blue-800 dark:text-blue-200">
            <strong>Usage:</strong> Use this token in your MCP client as a path parameter:
            <code class="bg-blue-100 dark:bg-blue-900 px-1 rounded">/mcp/YOUR_TOKEN/...</code>
          </p>
        </div>
      </div>
    </div>

    <!-- Available Endpoints -->
    <div class="bg-card rounded-lg border p-6">
      <h4 class="text-md font-semibold mb-4">Available MCP Endpoints</h4>
      <div v-if="isLoadingEndpoints" class="text-center py-4">
        <svg
          class="mx-auto h-6 w-6 animate-spin text-muted-foreground"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p class="mt-2 text-sm text-muted-foreground">Loading endpoints...</p>
      </div>
      <div v-else-if="mcpEndpoints && mcpEndpoints.tools && mcpEndpoints.tools.length > 0" class="space-y-3">
        <div
          v-for="tool in mcpEndpoints.tools"
          :key="tool.name"
          class="p-4 bg-muted/50 rounded-md border"
        >
          <div class="flex items-start justify-between gap-4">
            <div class="flex-1">
              <h5 class="text-sm font-semibold mb-1">{{ tool.name }}</h5>
              <p class="text-sm text-muted-foreground mb-2">{{ tool.description }}</p>
              <div v-if="tool.inputSchema.properties && Object.keys(tool.inputSchema.properties).length > 0" class="mt-2">
                <p class="text-xs font-medium text-muted-foreground mb-1">Parameters:</p>
                <ul class="text-xs text-muted-foreground space-y-1 ml-4">
                  <li v-for="(param, paramName) in tool.inputSchema.properties" :key="paramName">
                    <code class="text-xs bg-background px-1 rounded">{{ paramName }}</code>
                    <span class="ml-1">({{ param.type }})</span>
                    <span v-if="tool.inputSchema.required.includes(paramName)" class="ml-1 text-red-500">*</span>
                    <span v-if="param.description" class="ml-1">- {{ param.description }}</span>
                  </li>
                </ul>
              </div>
              <div v-else class="text-xs text-muted-foreground mt-1">No parameters required</div>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="text-sm text-muted-foreground">
        <p v-if="!displayedToken">Generate a token to see available endpoints</p>
        <p v-else>Unable to load endpoints. Please try again.</p>
      </div>
    </div>

    <!-- Instructions -->
    <div class="bg-card rounded-lg border p-6">
      <h4 class="text-md font-semibold mb-4">How to Use Your Access Token</h4>
      <div class="space-y-3 text-sm text-muted-foreground">
        <p>
          Your MCP access token allows you to securely access your positions, market data, and forecasts through the Model Context Protocol.
        </p>
        <ul class="list-disc list-inside space-y-2 ml-2">
          <li>Generate a token to get started with MCP integration</li>
          <li>Save your token securely - it will only be shown once after generation</li>
          <li>Regenerate your token if it's been compromised or you've lost it</li>
          <li>Use the token in your MCP client configuration</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { apiClient, type AccessTokenMetadataResponse, type AccessTokenResponse, type MCPEndpointsResponse } from '@/lib/api'

// Reactive data
const isLoading = ref(false)
const isGenerating = ref(false)
const isRegenerating = ref(false)
const tokenMetadata = ref<AccessTokenMetadataResponse>({
  has_token: false,
  created_at: null
})
const displayedToken = ref<string | null>(null)
const copied = ref(false)
const mcpEndpoints = ref<MCPEndpointsResponse | null>(null)
const isLoadingEndpoints = ref(false)

// Methods
const loadTokenMetadata = async () => {
  isLoading.value = true
  try {
    tokenMetadata.value = await apiClient.getTokenMetadata()
  } catch (error) {
    console.error('Failed to load token metadata:', error)
  } finally {
    isLoading.value = false
  }
}

const generateToken = async () => {
  isGenerating.value = true
  try {
    const response: AccessTokenResponse = await apiClient.generateAccessToken()
    displayedToken.value = response.access_token
    copied.value = false
    await loadTokenMetadata()
  } catch (error: any) {
    console.error('Failed to generate token:', error)
    if (error.response?.data?.detail) {
      alert(error.response.data.detail)
    } else {
      alert('Failed to generate token. Please try again.')
    }
  } finally {
    isGenerating.value = false
  }
}

const regenerateToken = async () => {
  if (!confirm('Are you sure you want to regenerate your token? The old token will no longer work.')) {
    return
  }

  isRegenerating.value = true
  try {
    const response: AccessTokenResponse = await apiClient.regenerateAccessToken()
    displayedToken.value = response.access_token
    copied.value = false
    await loadTokenMetadata()
  } catch (error: any) {
    console.error('Failed to regenerate token:', error)
    if (error.response?.data?.detail) {
      alert(error.response.data.detail)
    } else {
      alert('Failed to regenerate token. Please try again.')
    }
  } finally {
    isRegenerating.value = false
  }
}

const copyToken = async () => {
  if (!displayedToken.value) return

  try {
    await navigator.clipboard.writeText(displayedToken.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (error) {
    console.error('Failed to copy token:', error)
    alert('Failed to copy token. Please copy it manually.')
  }
}

const closeTokenDisplay = () => {
  displayedToken.value = null
  copied.value = false
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return date.toLocaleString()
  } catch {
    return dateString
  }
}

const loadMCPEndpoints = async (token: string) => {
  isLoadingEndpoints.value = true
  try {
    mcpEndpoints.value = await apiClient.getMCPEndpoints(token)
  } catch (error: any) {
    console.error('Failed to load MCP endpoints:', error)
    // Don't show error to user, just log it
    mcpEndpoints.value = null
  } finally {
    isLoadingEndpoints.value = false
  }
}

// Watch for displayed token changes to load endpoints
watch(displayedToken, (newToken) => {
  if (newToken) {
    loadMCPEndpoints(newToken)
  }
})

// Lifecycle
onMounted(() => {
  loadTokenMetadata()
  // If we have a displayed token, load endpoints
  if (displayedToken.value) {
    loadMCPEndpoints(displayedToken.value)
  }
})
</script>

